import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserVrmRegistrationSource } from "./user-vrm-registration"

/**
 * 運営指定NGの VRM を itemId 単位で永続化するグローバルNGリスト。
 *
 * `users/{userId}/vrm-registrations/{registrationId}` の `vrm_validation_status = "banned"` は
 * 登録ドキュメントに紐づくため、VC 連携解除で登録ごと削除されると失われ、再連携・再登録で
 * 復活してしまう。そこで運営指定NGの「真実の源」をこのコレクションに分離し、
 * ドキュメントID = itemId で永続化する。連携解除や登録の再作成をまたいでもNGが残る。
 *
 * - ドキュメントID = ソース側の itemId（source='vc' のときは VC の itemId）。
 * - このリストに存在する itemId は、全ユーザーに対して登録・ルック設定・再検証が禁止される（グローバルNG）。
 */
export const VrmNgItemDocumentMeta = z.object({
  item_id: z.string().describe("ソース側のアイテムID。source='vc' のときは VC の itemId。ドキュメントIDと同値。"),
  source: UserVrmRegistrationSource.describe("VRM 提供元。'vc' = VirtualCast。将来他ソース追加時に拡張する。"),
  vrm_name: z.string().optional().describe("NG設定時点の VRM 表示名（非正規化・参照用）。"),
  vrm_icon: z.string().optional().describe("NG設定時点の VRM サムネイル画像 URL（非正規化・参照用）。登録の icon、無ければ vc_icon を snapshot する。"),
  banned_by_user_id: z.string().describe("運営指定NGを設定した管理者の ID。"),
})

export type VrmNgItemDocumentMeta = z.infer<typeof VrmNgItemDocumentMeta>

export const VrmNgItemDocumentBody = VrmNgItemDocumentMeta.extend({
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type VrmNgItemDocumentBody = z.infer<typeof VrmNgItemDocumentBody>

export type VrmNgItemDocument = VrmNgItemDocumentBody & {
  id: string
}

export class VrmNgItemDocuments {

  static toDocumentData(doc: VrmNgItemDocument): object {
    return {
      ...doc,
      // id は document path (= itemId) に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): VrmNgItemDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: doc.id,
      item_id: data["item_id"],
      source: data["source"],
      vrm_name: data["vrm_name"],
      vrm_icon: data["vrm_icon"],
      banned_by_user_id: data["banned_by_user_id"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
